/*
 * Copyright (c) 2020. FINN Sp. z o.o.
 */

package pl.finn.forms.sekap1794

import io.kvision.core.*
import io.kvision.form.FormHorizontalRatio
import io.kvision.form.FormType
import io.kvision.form.check.RadioGroup
import io.kvision.form.formPanel
import io.kvision.form.text.Text
import io.kvision.form.text.TextArea
import io.kvision.form.text.textInput
import io.kvision.form.time.DateTime
import io.kvision.form.upload.uploadInput
import io.kvision.html.*
import io.kvision.modal.Alert
import io.kvision.modal.Confirm
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.state.bind
import io.kvision.table.*
import io.kvision.utils.px
import pl.finn.forms.POLE_WYMAGANE
import pl.finn.forms.sekap1794.Model.formularzStore

class Sekap1794Panel : SimplePanel() {
  val rozszerzeniaZalacznikow = setOf(
    "7z", "avi", "CAdES", "css", "csv", "dgn", "doc",
    "docx", "dwf", "dwg", "dxf", "geotiff", "gif", "gml", "gz", "gzip", "html",
    "jp2", "jpeg", "jpg", "m4a", "mp3", "mp4", "mpeg", "mpeg4", "mpg", "odp", "ods",
    "odt", "ogg", "ogv", "ott", "PAdES", "pdf", "png", "ppt", "pptx", "rng", "rtf",
    "svg", "tar", "tif", "tiff", "tls", "txt", "wav", "XAdES", "xhtml", "xls",
    "xlsx", "xml", "XMLenc", "XMLsig", "xps", "xsd", "xsl", "xslt", "zip"
  )

  init {
    bind(formularzStore) { state ->
      main {
        margin = 15.px
        id = "peupcontent"
        state.bladZapisu?.let {
          div(it, className = "alert alert-danger") {
            role = "alert"
          }
        }
        when (state.widok) {
          FormularzWidok.EDYCJA, FormularzWidok.NOWY -> {
            h1(
              "Korespondencja w sprawie projektu finansowanego ze środków EFRR w ramach RPO WSL na lata 2014-2020",
              className = "h4 text-center"
            ) {
              tabindex = -1
            }
            if (state.podmiotNazwa != null || state.podmiotMiejscUrz != null) {
              hPanel(justify = JustifyContent.FLEXEND) {
                marginBottom = 10.px
                vPanel {
                  div {
                    b(state.podmiotNazwa)
                  }
                  div(state.podmiotMiejscUrz)
                }
              }
            }
            if (state.form != null) {
              lateinit var numerWniosku: Text
              val formPanel = formPanel<Sekap1794Form>(
                type = FormType.HORIZONTAL, horizRatio = FormHorizontalRatio.RATIO_3,
                condensed = true
              ) {
                add(
                  Sekap1794Form::miejscowosc,
                  Text(label = "Miejscowość"),
                  required = true,
                  requiredMessage = POLE_WYMAGANE
                )
                add(Sekap1794Form::data, DateTime(label = "Data", format = "YYYY-MM-DD"))
                add(
                  Sekap1794Form::nazwaPodmiotu,
                  Text(label = "Nazwa podmiotu"),
                  required = true,
                  requiredMessage = POLE_WYMAGANE
                )
                add(
                  Sekap1794Form::adresEmail,
                  Text(label = "Adres poczty elektronicznej"),
                  required = true,
                  requiredMessage = POLE_WYMAGANE
                )
                add(Sekap1794Form::adresKodPocztowy, Text(label = "Kod pocztowy"))
                add(Sekap1794Form::adresMiejscowosc, Text(label = "Miejscowość"))
                add(Sekap1794Form::adresUlica, Text(label = "Ulica"))
                add(Sekap1794Form::adresBudynek, Text(label = "Numer domu"))
                add(Sekap1794Form::adresLokal, Text(label = "Numer lokalu"))
                add(
                  Sekap1794Form::dotyczyWniosku,
                  RadioGroup(
                    listOf("1" to "Jednego wniosku", "2" to "Wielu wniosków"),
                    label = "Korespondencja dotyczy", inline = true
                  ).apply {
                    onEvent {
                      change = {
                        if (self.value == "1") {
                          numerWniosku.show()
                        } else {
                          numerWniosku.hide()
                        }
                      }
                    }
                  }
                )
                add(
                  Sekap1794Form::numerWniosku,
                  Text(label = "Numer wniosku").apply {
                    numerWniosku = this
                    visible = state.form.dotyczyWniosku == "1"
                  },
                  required = true,
                  requiredMessage = POLE_WYMAGANE,
                  validatorMessage = { "Numer musi mieć 32 znaki" }) {
                  it.value?.length == 32
                }
                add(
                  Sekap1794Form::dotyczyCzego,
                  RadioGroup(
                    listOf(
                      "1" to "Złożenie wniosku",
                      "2" to "Korekta wniosku",
                      "3" to "Inna sprawa"
                    ), label = "Czego dotyczy", inline = true
                  )
                )
                add(
                  Sekap1794Form::temat,
                  Text(label = "Temat pisma"),
                  required = true,
                  requiredMessage = POLE_WYMAGANE
                )
                add(
                  Sekap1794Form::tresc,
                  TextArea(label = "Treść pisma", rows = 10),
                  required = true,
                  requiredMessage = POLE_WYMAGANE
                )
                div("Możesz dołączyć do pisma załączniki")
                tag(
                  TAG.SMALL,
                  "Dozwolone rozszerzenia plików to: ${rozszerzeniaZalacznikow.map { ".$it" }.joinToString()}."
                )
                if (!state.zalaczniki.isNullOrEmpty()) {
                  table(
                    listOf("Nazwa pliku", "Opis załącznika", ""),
                    setOf(TableType.HOVER, TableType.STRIPED, TableType.BORDERED, TableType.SMALL),
                    "Lista załączników do pisma",
                    responsiveType = ResponsiveType.RESPONSIVE
                  ) {
                    state.zalaczniki.forEach { zalacznik ->
                      row {
                        cell {
                          verticalAlign = VerticalAlign.MIDDLE
                          b(zalacznik.nazwa)
                        }
                        cell {
                          verticalAlign = VerticalAlign.MIDDLE
                          zalacznik.opis?.let {
                            div(it)
                          }
                        }
                        cell {
                          align = Align.CENTER
                          width = 150.px
                          button("Usuń załącznik", "fas fa-trash", ButtonStyle.DANGER) {
                            size = ButtonSize.SMALL
                            onClick {
                              Model.usunZalacznik(
                                zalacznik.nazwa,
                                this@formPanel.getData()
                              )
                            }
                          }
                        }
                      }
                    }
                  }
                }
                hPanel(spacing = 10, wrap = FlexWrap.WRAP, className = "wrap-panel") {
                  val zalacznik = uploadInput("/") {
                    explorerTheme = true
                    showUpload = false
                    showCancel = false
                    showPreview = false
                    dropZoneEnabled = false
                    allowedFileExtensions = rozszerzeniaZalacznikow
                  }
                  val opisZalacznika = textInput {
                    width = 300.px
                    placeholder = "Wprowadź opis załącznika"
                  }
                  button("Dodaj", "fas fa-upload").onClick {
                    Model.dodajZalacznik(
                      zalacznik,
                      opisZalacznika.value,
                      this@formPanel.getData()
                    )
                  }
                }
                hPanel(
                  spacing = 10,
                  alignItems = AlignItems.CENTER,
                  wrap = FlexWrap.WRAP, className = "wrap-panel"
                ) {
                  marginTop = 15.px
                  button(
                    "Zapisz dokument do podpisania i wysłania",
                    "fas fa-check",
                    ButtonStyle.SUCCESS
                  ).onClick {
                    val validateResult =
                      if (!this@formPanel.validate()) "Dokument zawiera niepoprawne dane." else null
                    Model.zapisz(this@formPanel.getData(), validateResult)
                  }
                  button(
                    "Sprawdź dokument",
                    "fas fa-tasks"
                  ).onClick {
                    if (this@formPanel.validate()) {
                      Alert.show(
                        "Sprawdzenie dokumentu",
                        "Dokument jest poprawny.<br/>Wszystkie pola wypełnione są prawidłowo.",
                        rich = true
                      )
                    } else {
                      Alert.show(
                        "Sprawdzenie dokumentu",
                        "Dokument zawiera niepoprawne dane.<br/>Należy poprawić pola oznaczone na czerwono.",
                        rich = true
                      )
                    }
                  }
                  button("Podgląd dokumentu", "fas fa-eye").onClick {
                    Model.podglad(this@formPanel.getData(), this)
                  }
                  button(
                    "Anuluj",
                    "fas fa-times",
                    ButtonStyle.SECONDARY
                  ).onClick {
                    Confirm.show(
                      "Potwierdzenie",
                      "Czy anulować edycję formularza i powrócić do poprzedniej strony?",
                      yesTitle = "Tak",
                      noTitle = "Nie"
                    ) {
                      Model.anuluj()
                    }
                  }
                }
              }
              formPanel.setData(state.form)
            }
          }
          else -> {
          }
        }
      }
    }
  }
}
